import {
  ProjectType,
  isContentAndLanguage,
  isTaskType2D,
  isTaskTypeLidar,
  BusinessUnit,
  BusinessUnitProduct,
} from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { SectionTitle } from 'corp/components/QMWorkflow/QMWorkflowTypes';
import {
  MainSidebarMenu,
  ChatTaskMenu,
  ThroughputMenu,
  ProjectConfigMenu,
  FinanceTabs,
  BenchmarkCenterTabs,
  TrainingMenu,
  DataQualityTabs,
  AllocationAndQualificationsTabs,
  PipelineTabs,
  QualityMenu,
} from 'corp/views/CorpProjectGroupManager/constants';
import { H3 } from 'design-system/Typography';
import { makeStyles } from 'design-system/styles';
import { useRouter } from 'next/router';
import useQMProjectConfig from 'react-scale/corp/hooks/qm/useQMProjectConfig';
import useQMProject from 'react-scale/corp/hooks/qm/useQMProject';
import { isTaxonomyManagementConfigEnabled } from 'react-scale/lib/dashboard-api/contentUnderstanding';
import { useAsync } from 'react-use-v13';
import { problemsSection } from './QMWorkflowDefinitions';
import { ActionItem, ISubSection } from './QMWorkflowTypes';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ALLOWED_PRODUCT_TYPES } from '@scale/shared/taxonomyService/dto/constants';
import { GenAIOpsHubLogo } from 'react-scale/corp/views/CorpProjectGroupManager/components/GenAIOpsHubLogo';

const NavItemElem = (props: { title: string; activeScrollName?: string }) => {
  const classes = useStyles();

  const computedStyle =
    props.activeScrollName === props.title ? classes.selectedNavItemElement : classes.navItemElem;

  return (
    <div>
      <a className={computedStyle} href={`#${props.title.replace(/\s+/g, '').toLowerCase()}`}>
        {props.title}
      </a>
    </div>
  );
};

const NavNextJSItemElem = (props: {
  title: string;
  link: string;
  activeScrollName?: string;
  redirectsToOpsHub?: boolean;
}) => {
  const classes = useStyles();
  const router = useRouter();
  const handleClick = () => {
    router.push(props.link);
  };

  const computedStyle =
    props.activeScrollName === props.title ? classes.selectedNavItemElement : classes.navItemElem;

  return (
    <div onClick={handleClick}>
      <a className={`${computedStyle}`} style={{ display: 'flex', alignItems: 'start' }}>
        {props.redirectsToOpsHub && (
          <div style={{ height: 23, width: 23, marginLeft: -5, marginTop: -2 }}>
            <GenAIOpsHubLogo />
          </div>
        )}
        <div>{props.title}</div>
      </a>
    </div>
  );
};

const NavItem = (props: {
  projectId: string;
  sectionTitle: string;
  sectionChildren: ISubSection[];
  activeScrollName?: string;
  setActionItem?: (actionItem: ActionItem | undefined) => void;
}) => {
  const classes = useStyles();
  const router = useRouter();

  const handleClick = () => {
    props.setActionItem && props.setActionItem(undefined);
    if (props.sectionTitle === SectionTitle.Problems) {
      router.push(`/corp/qm/queue/${props.projectId}/problems`);
    } else {
      router.push(`/corp/qm/queue/${props.projectId}/overview`);
    }
  };

  return (
    <div>
      {props.sectionTitle && (
        <div onClick={handleClick}>
          <div className={classes.navSection}>
            <H3>{props.sectionTitle.toUpperCase()}</H3>
          </div>
          {props.sectionChildren.map(child => (
            <NavItemElem
              title={child.title}
              activeScrollName={props.activeScrollName}
              key={props.sectionTitle + child.title}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line complexity
const Nav = (props: {
  projectId: string;
  taskType: TaskType;
  taskTemplateVersion: number;
  hasBaseProject: boolean;
  projectType?: ProjectType;
  activeScrollName?: string;
  setActionItem?: (actionItem: ActionItem | undefined) => void;
  quickLinterTaskTypes: Set<TaskType>;
  usesPipelineV3?: boolean;
  taxonomyByReviewLevelsTypes: Set<TaskType>;
}) => {
  const {
    projectId,
    activeScrollName,
    taskType,
    taskTemplateVersion,
    hasBaseProject,
    quickLinterTaskTypes,
    usesPipelineV3,
    taxonomyByReviewLevelsTypes,
  } = props;

  const { projectConfig } = useQMProjectConfig(projectId);
  const { project } = useQMProject(projectId);
  const isGenAIProject =
    project?.businessUnit === BusinessUnit.GenerativeAIDataEngine &&
    project?.businessUnitProduct === BusinessUnitProduct.RLHF;

  const classes = useStyles();

  const isContentAndLanguageTask = isContentAndLanguage(taskType);

  const showArchetypeSelector = isContentAndLanguageTask && taskTemplateVersion === -1;
  const showTaskParamsEditor = [
    TaskType.DocumentTranscription,
    TaskType.NamedEntityRecognition,
    TaskType.GenericDataCollection,
    TaskType.ImageAnnotation,
  ].includes(taskType);

  const showTaskTemplatesEditor = isContentAndLanguageTask;
  const showSpecializedReviewRouting = isContentAndLanguageTask;

  const useOntologyVersioning = useUnleashFeatureFlag(
    'enable-ontology-versioning',
    { projectId },
    false,
  );
  const { value: valueOntologyManager } = useAsync(
    async () => isTaxonomyManagementConfigEnabled(projectId),
    [projectId],
  );
  const showOntologyManager = useOntologyVersioning || valueOntologyManager;

  const showCreateDocumentTasksPage = taskType === TaskType.DocumentTranscription;
  const showPipelineEditorPage =
    !usesPipelineV3 ||
    [
      TaskType.TextCollection,
      TaskType.Chat,
      TaskType.MultiChat,
      TaskType.ChatExperimental,
      TaskType.ChatV2,
      TaskType.SensorFusion,
      TaskType.CruiseControl,
    ].includes(taskType);
  const showGraderConfigsEditorPage =
    taskType === TaskType.TextCollection ||
    isTaskType2D(taskType) ||
    (isTaskTypeLidar(taskType) && taskType !== TaskType.LidarLinking);
  const showBenchmarkSpeedAuditPage = taskType === TaskType.TextCollection;
  const showAVTaxonomyService = !hasBaseProject && ALLOWED_PRODUCT_TYPES.includes(taskType);

  const showQueryPlaygroundPage = isContentAndLanguageTask;
  const showNextGenInstructions =
    props.projectType === ProjectType.SelfServe ||
    projectConfig?.config?.nextGenInstructionsConfig?.enabled.value;

  const enableProjectDocumentsAndAnnouncements = useUnleashFeatureFlag(
    'project-documents-announcements',
    { projectId },
    false,
  );

  return (
    <div>
      <div>
        <div className={classes.navSection}>
          <H3>Project Setup</H3>
        </div>
        {showArchetypeSelector && (
          <NavNextJSItemElem
            title="Archetype Selector"
            link={`/corp/qm/queue/${projectId}/archetypes`}
            activeScrollName={activeScrollName}
          />
        )}
        {showOntologyManager && (
          <NavNextJSItemElem
            title="Ontology Manager"
            link={`/corp/qm/queue/${projectId}/ontologyManager`}
            activeScrollName={activeScrollName}
          />
        )}
        {showAVTaxonomyService && (
          <NavNextJSItemElem
            title={SectionTitle.AVTaxonomyService}
            link={`/corp/qm/queue/${projectId}/avTaxonomyService`}
            activeScrollName={activeScrollName}
          />
        )}
        <NavNextJSItemElem
          title="VLM Prelabels Prompt Management"
          link={`/corp/qm/queue/${projectId}/vlmPrelabelsPromptManagement`}
          activeScrollName={activeScrollName}
        />
        {showTaskParamsEditor && (
          <NavNextJSItemElem
            title="Task Params Editor"
            link={`/corp/qm/queue/${projectId}/params`}
            activeScrollName={activeScrollName}
          />
        )}
        {showCreateDocumentTasksPage && (
          <NavNextJSItemElem
            title="Document Task Creator"
            link={`/corp/qm/queue/${projectId}/documentTaskCreator`}
            activeScrollName={activeScrollName}
          />
        )}
        {showPipelineEditorPage && isGenAIProject && (
          <NavNextJSItemElem
            title="Pipeline Editor"
            link={`/corp/genai-ops-hub/projects/${projectId}/${MainSidebarMenu.ProjectSetup}/${ThroughputMenu.PipelineEditor}/${PipelineTabs.Editor}`}
            activeScrollName={activeScrollName}
            redirectsToOpsHub
          />
        )}
        {isGenAIProject && (
          <NavNextJSItemElem
            title="Project Roles and Responsibilities"
            link={`/corp/genai-ops-hub/projects/${projectId}/${MainSidebarMenu.Overview}`}
            activeScrollName={activeScrollName}
            redirectsToOpsHub
          />
        )}
        <NavNextJSItemElem
          title={SectionTitle.StaffingExceptionForm}
          link={`/corp/qm/queue/${projectId}/staffingExceptionForm`}
          activeScrollName={activeScrollName}
        />
        {showGraderConfigsEditorPage && (
          <NavNextJSItemElem
            title={SectionTitle.GraderConfigsEditor}
            link={`/corp/qm/queue/${projectId}/graderConfigsEditor`}
            activeScrollName={activeScrollName}
          />
        )}
        <NavNextJSItemElem
          title={SectionTitle.BillingVersionEditor}
          link={`/corp/qm/queue/${projectId}/billingVersionEditor`}
          activeScrollName={activeScrollName}
        />
        {showTaskTemplatesEditor && isGenAIProject && (
          <NavNextJSItemElem
            title="Task Templates Editor"
            link={`/corp/genai-ops-hub/projects/${projectId}/${MainSidebarMenu.ProjectSetup}/${ChatTaskMenu.Taxonomy}`}
            activeScrollName={activeScrollName}
            redirectsToOpsHub
          />
        )}
        {showNextGenInstructions && (
          <>
            {isGenAIProject && (
              <NavNextJSItemElem
                title={SectionTitle.InstructionsTaxonomy}
                link={`/corp/genai-ops-hub/projects/${projectId}/${MainSidebarMenu.Enablement}/${TrainingMenu.Guidelines}`}
                activeScrollName={activeScrollName}
                redirectsToOpsHub
              />
            )}
            <NavNextJSItemElem
              title={SectionTitle.ProjectTaxonomyVersions}
              link={`/corp/qm/queue/${projectId}/versions`}
              activeScrollName={activeScrollName}
            />
          </>
        )}
        <NavNextJSItemElem
          title={SectionTitle.PayRateCardPreview}
          link={`/corp/qm/queue/${projectId}/payRateCardPreview`}
        />
        {isGenAIProject && (
          <NavNextJSItemElem
            title={SectionTitle.Qualifications}
            link={`/corp/genai-ops-hub/projects/${projectId}/${MainSidebarMenu.ContributorManagement}/${ProjectConfigMenu.Qualifications}/${AllocationAndQualificationsTabs.Qualifications}`}
            activeScrollName={activeScrollName}
            redirectsToOpsHub
          />
        )}
        {showPipelineEditorPage && (
          <NavNextJSItemElem
            title={`Pipeline Editor ${isGenAIProject ? '(Old)' : ''}`}
            link={`/corp/qm/queue/${projectId}/pipeline`}
            activeScrollName={activeScrollName}
          />
        )}
        <NavNextJSItemElem
          title="Project Roles and Responsibilities (Old)"
          link={`/corp/qm/queue/${projectId}/owners`}
          activeScrollName={activeScrollName}
        />
        {showTaskTemplatesEditor && (
          <NavNextJSItemElem
            title="Task Templates Editor (Old)"
            link={`/corp/qm/queue/${projectId}/templates`}
            activeScrollName={activeScrollName}
          />
        )}
        {showNextGenInstructions && (
          <NavNextJSItemElem
            title={SectionTitle.InstructionsTaxonomy + ' (Old)'}
            link={`/corp/qm/queue/${projectId}/instructionsTaxonomy`}
            activeScrollName={activeScrollName}
          />
        )}
        <NavNextJSItemElem
          title={SectionTitle.Qualifications + ' (Old)'}
          link={`/corp/qm/queue/${projectId}/qualifications`}
          activeScrollName={activeScrollName}
        />
      </div>
      {enableProjectDocumentsAndAnnouncements && (
        <div className={classes.navSection}>
          <H3>Outlier</H3>
          <NavNextJSItemElem
            title={SectionTitle.ProjectDocumentsAndAnnouncements}
            link={`/corp/qm/queue/${projectId}/projectDocumentsAndAnnouncements`}
            activeScrollName={activeScrollName}
          />
        </div>
      )}
      <div className={classes.navSection}>
        <H3>Configs</H3>
        <NavNextJSItemElem
          title="Disabling & Linting"
          link={`/corp/qm/queue/${projectId}/quality`}
          activeScrollName={activeScrollName}
        />
        <NavNextJSItemElem
          title="Full Configs"
          link={`/corp/qm/queue/${projectId}/config`}
          activeScrollName={activeScrollName}
        />
        {showSpecializedReviewRouting && (
          <NavNextJSItemElem
            title={SectionTitle.SpecializedReviewRouting}
            link={`/corp/qm/queue/${projectId}/specializedReviewRouting`}
            activeScrollName={activeScrollName}
          />
        )}
        {quickLinterTaskTypes.has(taskType) && (
          <NavNextJSItemElem
            title="Quick Linters"
            link={`/corp/qm/queue/${projectId}/quick-linters`}
            activeScrollName={activeScrollName}
          />
        )}
        {taxonomyByReviewLevelsTypes.has(taskType) && (
          <NavNextJSItemElem
            title="Taxonomy by Review Levels"
            link={`/corp/qm/queue/${projectId}/taxonomy-by-review-levels`}
            activeScrollName={activeScrollName}
          />
        )}
      </div>
      <div className={classes.navSection}>
        <H3>Data &amp; Monitoring</H3>
      </div>
      <NavNextJSItemElem
        title="Tasks"
        link={`/corp/qm/queue/${props.projectId}/tasks`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Subtasks"
        link={`/corp/qm/queue/${props.projectId}/subtasks`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Throughput"
        link={`/corp/qm/queue/${props.projectId}/throughput`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Incident Flags"
        link={`/corp/qm/queue/${props.projectId}/incidentFlags`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="JSON Viewer"
        link={`/corp/qm/queue/${props.projectId}/jsonViewer`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title={SectionTitle.QualityMetrics}
        link={`/corp/qm/queue/${props.projectId}/qualityMetrics`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title={SectionTitle.WorkerPerformance}
        link={`/corp/qm/queue/${props.projectId}/workerPerformance`}
        activeScrollName={activeScrollName}
      />
      {isGenAIProject && (
        <NavNextJSItemElem
          title={`GenAI Ops Hub ${MainSidebarMenu.Quality}`}
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${MainSidebarMenu.Quality}/${DataQualityTabs.QualityMetricsReport}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      {showQueryPlaygroundPage && (
        <NavNextJSItemElem
          title={SectionTitle.QueryPlayground}
          link={`/corp/qm/queue/${props.projectId}/queryPlayground`}
          activeScrollName={activeScrollName}
        />
      )}
      <NavNextJSItemElem
        title={SectionTitle.ActionBatches}
        link={`/corp/qm/queue/${props.projectId}/actionBatches`}
        activeScrollName={activeScrollName}
      />
      {isGenAIProject && (
        <NavNextJSItemElem
          title={SectionTitle.PaySandbox}
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${MainSidebarMenu.Finance}/${FinanceTabs.PaySandbox}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      <NavNextJSItemElem
        title="Project channels/categories"
        link={`/corp/genai-ops-hub/community-management?project=${projectId}`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title={SectionTitle.PaySandbox + ' (Old)'}
        link={`/corp/qm/queue/${props.projectId}/paySandbox`}
        activeScrollName={activeScrollName}
      />
      <div className={classes.navSection}>
        <H3>Actions</H3>
      </div>
      {isGenAIProject && (
        <NavNextJSItemElem
          title={SectionTitle.DisableProject}
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${MainSidebarMenu.Overview}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      <NavNextJSItemElem
        title={SectionTitle.DisableProject + ' (Old)'}
        link={`/corp/qm/queue/${props.projectId}/disable`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title={SectionTitle.ActionDirectory}
        link={`/corp/qm/queue/${props.projectId}/actions`}
        activeScrollName={activeScrollName}
      />
      <div className={classes.navSection}>
        <H3>Project Levers</H3>
      </div>
      {isGenAIProject && (
        <NavNextJSItemElem
          title="Benchmark Center (Gen AI)"
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${QualityMenu.BenchmarkCenter}/${BenchmarkCenterTabs.ProjectBenchmarks}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      <NavNextJSItemElem
        title="Benchmarks"
        link={`/corp/qm/queue/${props.projectId}/benchmarks`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Prioritize"
        link={`/corp/qm/queue/${props.projectId}/priority`}
        activeScrollName={activeScrollName}
      />
      {isGenAIProject && (
        <NavNextJSItemElem
          title="Incentives"
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${MainSidebarMenu.Finance}/${FinanceTabs.StellarQualityIncentives}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      <NavNextJSItemElem
        title="Badges"
        link={`/corp/qm/queue/${props.projectId}/badges`}
        activeScrollName={activeScrollName}
      />
      {isGenAIProject && (
        <NavNextJSItemElem
          title="Training"
          link={`/corp/genai-ops-hub/projects/${props.projectId}/${MainSidebarMenu.Enablement}/${TrainingMenu.ActivationControlPanel}`}
          activeScrollName={activeScrollName}
          redirectsToOpsHub
        />
      )}
      <NavNextJSItemElem
        title="Incentives (Old)"
        link={`/corp/qm/queue/${props.projectId}/incentives`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Training (Old)"
        link={`/corp/qm/queue/${props.projectId}/training`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Zero Review + Spotter Pipeline"
        link={`/corp/qm/queue/${props.projectId}/spotterPipeline`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Autopilot"
        link={`/corp/qm/queue/${props.projectId}/autopilot`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Rules"
        link={`/corp/qm/queue/${props.projectId}/rules`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Reviewer Feedback"
        link={`/corp/qm/queue/${props.projectId}/reviewerFeedback`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Speed Audit"
        link={`/corp/qm/queue/${props.projectId}/speedAudit`}
        activeScrollName={activeScrollName}
      />
      {showBenchmarkSpeedAuditPage && (
        <NavNextJSItemElem
          title={SectionTitle.BenchmarkSpeedAudit}
          link={`/corp/qm/queue/${projectId}/benchmarkSpeedAudit`}
          activeScrollName={activeScrollName}
        />
      )}
      <NavNextJSItemElem
        title="Audit Dashboard"
        link={`/corp/qm/queue/${props.projectId}/auditDashboard`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title="Task Support Ticket"
        link={`/corp/support_ticket/${props.projectId}`}
        activeScrollName={activeScrollName}
      />
      <NavNextJSItemElem
        title={SectionTitle.TaskerOperations}
        link={`/corp/qm/queue/${props.projectId}/operations`}
        activeScrollName={activeScrollName}
      />
      <NavItem
        projectId={props.projectId}
        sectionTitle={problemsSection.title}
        sectionChildren={problemsSection.children}
        activeScrollName={activeScrollName}
        key={problemsSection.title}
        setActionItem={props.setActionItem}
      />
      <div className={classes.navMask} />
    </div>
  );
};

const useStyles = makeStyles({
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.7px',
    margin: 0,
    padding: 8,
    marginTop: 12,
  },
  navMask: {
    position: 'fixed',
    bottom: 0,
    width: 300,
    height: '50%',
    pointerEvents: 'none',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 80%, rgba(255,255,255,0.9) 100%)',
  },
  navItemElem: {
    display: 'block',
    color: 'black',
    padding: '2px 0px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    borderRadius: 4,
    opacity: 0.65,
    transition: 'all 120ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  selectedNavItemElement: {
    display: 'block',
    color: 'var(--color-RemoBlue)',
    padding: '2px 0px',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
    borderRadius: 4,
    opacity: 0.65,
    transition: 'all 120ms ease-out',
    '&:hover': {
      opacity: 1,
    },
  },
  navSection: {
    marginTop: 24,
  },
  externalLink: {
    color: 'black',
    fontSize: 14,
    fontWeight: 400,
  },
});
export default Nav;
